"use client";

import { useState, useEffect } from "react";
import { Plus, LayoutGrid, HelpCircle } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { useAuth } from "@/hooks/useAuth";
import MarketTile from "./components/MarketTile";
import EventTile from "./components/EventTile";
import MarketSearchModal from "./components/MarketSearchModal";
import WelcomeModal from "./components/WelcomeModal";
import { useSidebar } from "@/contexts/SidebarContext";
import { useT } from "@/hooks/useT";

interface DegenboxMarket {
  id: string;
  slug: string;
  title: string;
  image: string | null;
  yesPrice: number;
  noPrice: number;
  volume24hr: number;
  view: "orderbook" | "trades" | "chart";
  type: "market" | "event";
  markets?: any[];
  clobTokenIds?: string[];
}


export default function DegenboxContent() {
  const t = useT();
  const [markets, setMarkets] = useState<DegenboxMarket[]>([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { isCollapsed } = useSidebar();
  const { isReady, isLoggedIn, login } = useAuth();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setMounted(true);

    const saved = localStorage.getItem("degenbox_markets");
    if (saved) {
      setMarkets(JSON.parse(saved));
    }

    const dontShow = localStorage.getItem("degenbox_welcome_dont_show");
    const hasSeenWelcome = localStorage.getItem("degenbox_welcome_seen");
    if (!hasSeenWelcome && !dontShow) {
      setShowWelcome(true);
    }
  }, []);

  // Hide landing and block body scroll when dashboard is visible. The
  // first-paint hide is handled by the inline <script> in page.tsx
  // (id="__ff-degenbox-hide-landing"); here we mirror that at runtime
  // and tear the style tag back out if Privy resolves to logged-out
  // (stale optimistic flag).
  useEffect(() => {
    if (!mounted) return;

    const landing = document.querySelector(".degenbox-landing") as HTMLElement;
    const inlineStyle = document.getElementById("__ff-degenbox-hide-landing");

    if (isLoggedIn) {
      // Hide landing content and block scroll
      if (landing) landing.style.display = "none";
      document.body.style.overflow = 'hidden';
    } else {
      // Show landing content and restore scroll
      if (landing) landing.style.display = "block";
      if (inlineStyle) inlineStyle.remove();
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mounted, isLoggedIn]);

  useEffect(() => {
    if (mounted) {
      if (markets.length > 0) {
        localStorage.setItem("degenbox_markets", JSON.stringify(markets));
      } else {
        localStorage.removeItem("degenbox_markets");
      }
    }
  }, [markets, mounted]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setMarkets((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addMarket = (market: DegenboxMarket) => {
    if (markets.length < 8) {
      setMarkets([...markets, market]);
    }
  };

  const removeMarket = (id: string) => {
    setMarkets(markets.filter((m) => m.id !== id));
  };

  const toggleView = (id: string, view?: "orderbook" | "trades" | "chart") => {
    setMarkets(
      markets.map((m) => {
        if (m.id !== id) return m;
        if (view) return { ...m, view };
        const nextView = m.view === "orderbook" ? "trades" : m.view === "trades" ? "chart" : "orderbook";
        return { ...m, view: nextView };
      })
    );
  };

  const closeWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem("degenbox_welcome_seen", "true");
  };

  // Don't render overlay until mounted + auth ready (hero is visible underneath)
  if (!mounted || !isReady) return null;

  // Don't render dashboard when not logged in (show landing underneath)
  if (!isLoggedIn) return null;

  // Dashboard overlay - covers the landing when authenticated
  return (
    <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-[#0f0f12] overflow-hidden">
      {/* Adjust for sidebar */}
      <div className={`absolute inset-0 transition-all duration-300 ${isCollapsed ? 'lg:left-20' : 'lg:left-64'}`}>
        {/* Header spacer */}
        <div className="h-14 lg:h-16" />

        {/* Toolbar */}
        <div className="px-4 sm:px-6 lg:px-12 py-3 flex items-center justify-between border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[5px] bg-gray-900 dark:bg-white flex items-center justify-center">
              <LayoutGrid className="w-4 h-4 text-white dark:text-gray-900" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("degen.dash.title")}
            </h1>
            <span className="text-[11px] tabular-nums text-gray-500 bg-gray-100 dark:bg-[#0f0f12] px-2 py-0.5 rounded-[5px]">
              {markets.length}/8
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowWelcome(true)}
              className="p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
              aria-label={t("degen.dash.helpAria")}
              title={t("degen.dash.help")}
            >
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </button>
            {markets.length < 8 && (
              <button
                onClick={() => setShowSearchModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[5px] text-xs font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("degen.dash.addMarket")}
              </button>
            )}
          </div>
        </div>

        {markets.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center h-[calc(100dvh-8rem)] px-4">
            <div className="w-16 h-16 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center mb-6">
              <LayoutGrid className="w-8 h-8 text-gray-400" />
            </div>

            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
              {t("degen.dash.empty.title")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm mb-8">
              {t("degen.dash.empty.subtitle")}
            </p>

            <button
              onClick={() => setShowSearchModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors font-semibold"
            >
              <Plus className="w-5 h-5" />
              {t("degen.dash.empty.cta")}
            </button>
          </div>
        ) : (
          // Markets Grid
          <div className="h-[calc(100dvh-7.5rem)] overflow-auto">
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4 lg:py-6">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={markets.map((m) => m.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                    {markets.map((market) => (
                      market.type === "event" ? (
                        <EventTile
                          key={market.id}
                          market={market}
                          onRemove={removeMarket}
                        />
                      ) : (
                        <MarketTile
                          key={market.id}
                          market={market}
                          onRemove={removeMarket}
                          onToggleView={toggleView}
                        />
                      )
                    ))}

                    {markets.length < 8 && (
                      <button
                        onClick={() => setShowSearchModal(true)}
                        className="min-h-[400px] bg-gray-50 dark:bg-white/[0.02] rounded-[5px] border-2 border-dashed border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors flex flex-col items-center justify-center gap-3 group"
                      >
                        <div className="w-12 h-12 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-white/[0.08] transition-colors">
                          <Plus className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="text-center">
                          <p className="font-medium text-sm text-gray-900 dark:text-white mb-1">
                            {t("degen.dash.addMarket")}
                          </p>
                          <p className="text-[11px] text-gray-400 tabular-nums">
                            {markets.length}/8
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showSearchModal && (
        <MarketSearchModal
          onClose={() => setShowSearchModal(false)}
          onSelect={addMarket}
          existingMarkets={markets.map((m) => m.id)}
        />
      )}

      {showWelcome && <WelcomeModal onClose={closeWelcome} />}
    </div>
  );
}
